/**
 * Live post cards for homepage Featured Carousel from a
 * `template_components` Latest/Series tab (max 25).
 */

import React from "react";
import { useQuery } from "react-query";
import {
  FEATURED_CAROUSEL_POST_LIMIT,
  fetchFeaturedCarouselPostsForTab,
} from "./featuredCarouselPosts";
import type { CarouselData, FeaturedCarouselItem } from "./types";
import {
  clearPendingFeedStrip,
  peekPendingFeedStrip,
} from "./websiteEditorPendingFeedStrip";

export function useFeaturedCarouselStripItems(args: {
  enabled: boolean;
  userId: string;
  username: string;
  /** Selected Recent Content tab (`template_components`). */
  carousel: CarouselData | null | undefined;
  limit?: number;
  /** Collection / static cards when not loading a live post feed. */
  staticItems: FeaturedCarouselItem[];
  preferStatic: boolean;
  /** Add trending edit: clear tiles while refetching (matches ghost preview). */
  keepPreviousData?: boolean;
  /**
   * Editor recomputes trending from analytics. The public page uses the saved
   * daily post list so a stale `isEmpty` row can still render.
   */
  bypassTrendingSnapshot?: boolean;
}): {
  items: FeaturedCarouselItem[];
  isLoading: boolean;
  isFetching: boolean;
} {
  const {
    enabled,
    userId,
    username,
    carousel,
    limit = FEATURED_CAROUSEL_POST_LIMIT,
    staticItems,
    preferStatic,
    keepPreviousData = true,
    bypassTrendingSnapshot,
  } = args;

  const shouldFetch =
    enabled &&
    !preferStatic &&
    Boolean(userId && username && carousel?.renderSlug);

  const slug = String(carousel?.renderSlug || carousel?.id || "").trim();
  const pendingSeed = React.useMemo(
    () => (slug ? peekPendingFeedStrip(slug) : null),
    // Re-check when the bound carousel identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [slug, carousel?.postIds, carousel?.componentType],
  );

  const isTrendingFeed =
    String(carousel?.componentType || "").trim().toLowerCase() ===
      "trending content" ||
    String(carousel?.componentType || "").trim().toLowerCase() ===
      "trending products";
  const refreshTrending = bypassTrendingSnapshot ?? isTrendingFeed;
  const phraseType = String(carousel?.componentType || "").trim().toLowerCase();
  const isPhraseFeed =
    phraseType === "content-series" ||
    phraseType === "query-content" ||
    phraseType === "query-links";

  const queryKey = React.useMemo(
    () =>
      [
        "featured-carousel-strip",
        userId,
        username,
        carousel?.renderSlug || carousel?.id || "",
        // Trending strip is driven by `details` (min clicks + window) — same
        // as Add ghost preview. Snapshot post_ids must not trigger a refetch.
        // Hand-pick keeps postIds in the key so Update refetches ASC/DESC Order.
        isTrendingFeed ? "" : carousel?.postIds || "",
        carousel?.componentType || "",
        isTrendingFeed ? "" : carousel?.query || "",
        carousel?.contentOrder || "",
        String(carousel?.contentOrder || "").toUpperCase() === "CUSTOM"
          ? carousel?.customOrder || ""
          : "",
        carousel?.shoppableContentFilter || "",
        carousel?.shoppableStatusFilter || "",
        carousel?.contentShape || "",
        carousel?.details || "",
        // Bumps the cache when shop clicks move to template_analytics.
        String(carousel?.componentType || "").trim().toLowerCase() ===
          "trending products"
          ? "template-shop"
          : String(carousel?.componentType || "").trim().toLowerCase() ===
              "trending content"
            ? "template-content"
            : "",
        refreshTrending ? "live" : "snap",
        limit,
      ] as const,
    [userId, username, carousel, limit, isTrendingFeed, refreshTrending],
  );

  const query = useQuery(
    queryKey,
    () =>
      fetchFeaturedCarouselPostsForTab({
        userId,
        username,
        carousel: carousel!,
        limit,
        bypassTrendingSnapshot: refreshTrending,
      }),
    {
      enabled: shouldFetch,
      staleTime:
        String(carousel?.componentType || "").trim().toLowerCase() ===
          "trending content" && !keepPreviousData
          ? 0
          : 60_000,
      refetchOnWindowFocus: false,
      keepPreviousData,
    },
  );

  React.useEffect(() => {
    if (slug && query.data && query.data.length > 0) {
      clearPendingFeedStrip(slug);
    }
  }, [slug, query.data]);

  const isFetching = Boolean(query.isFetching);

  if (preferStatic) {
    return { items: staticItems, isLoading: false, isFetching: false };
  }
  if (!shouldFetch) {
    if (query.data && query.data.length > 0) {
      return { items: query.data, isLoading: false, isFetching: false };
    }
    if (pendingSeed?.length) {
      return { items: pendingSeed, isLoading: false, isFetching: false };
    }
    if (carousel && userId && username) {
      return { items: [], isLoading: true, isFetching: false };
    }
    return { items: staticItems, isLoading: false, isFetching: false };
  }
  if (query.data && query.data.length > 0) {
    return { items: query.data, isLoading: false, isFetching };
  }
  // Settled empty phrase search: do not substitute the Add preview or static cards.
  if (isPhraseFeed && Array.isArray(query.data) && query.isFetched) {
    return { items: [], isLoading: false, isFetching };
  }
  // While the live feed is in flight, keep the Add preview thumbs (one tile
  // or many). Do not invent extra copies of a single cover.
  // Podcast Banner may use a single pending episode thumb from Add preview.
  const isPodcastBanner =
    String(carousel?.componentType || "")
      .trim()
      .toLowerCase() === "latest-podcast" ||
    String(carousel?.componentDisplayName || "")
      .trim()
      .toLowerCase() === "latest-podcast" ||
    // FeaturedCarousel passes limit=1 for Banner layout (incl. style-based).
    limit === 1;
  // Idle / in-flight with no tiles yet is a skeleton, not "No data".
  if ((query.isLoading || query.isIdle || !query.isFetched) && !query.data) {
    if (isPodcastBanner && pendingSeed?.length) {
      return { items: pendingSeed.slice(0, 1), isLoading: false, isFetching };
    }
    if (!isPodcastBanner && pendingSeed && pendingSeed.length >= 1) {
      return { items: pendingSeed, isLoading: false, isFetching };
    }
    return { items: [], isLoading: true, isFetching: true };
  }
  if (Array.isArray(query.data) && query.data.length === 0 && query.isFetched && !query.isFetching) {
    return { items: [], isLoading: false, isFetching: false };
  }
  if (query.isFetching && !(query.data && query.data.length > 0)) {
    if (isPodcastBanner && pendingSeed?.length) {
      return { items: pendingSeed.slice(0, 1), isLoading: false, isFetching };
    }
    if (!isPodcastBanner && pendingSeed && pendingSeed.length >= 1) {
      return { items: pendingSeed, isLoading: false, isFetching };
    }
    return { items: [], isLoading: true, isFetching: true };
  }
  if (isPodcastBanner && pendingSeed?.length && !(query.data && query.data.length)) {
    return { items: pendingSeed.slice(0, 1), isLoading: false, isFetching };
  }
  if (!isPodcastBanner && pendingSeed?.length) {
    return { items: pendingSeed, isLoading: false, isFetching };
  }
  return { items: staticItems, isLoading: false, isFetching };
}
